// The pieces the Hiring screens share, on the Capability design system
// (../capability-setup/designSystem.tsx), so Hiring looks like the rest of the
// admin side rather than a second product.
//
// Three of these carry compliance weight and are not decoration:
//   CopyLines renders the wording that is under legal review, from the server,
//   so legal's edits land without a front-end change (PRD 8.3, 8.4).
//   RationaleField is the decision reason Hire and Close require, with the
//   tooltip that says why (H-26, L-3).
//   IntegrityCaption is the label that has to appear wherever a response time
//   or an input mode is shown (L-6).

import React from 'react';
import { Box, Typography } from '@mui/material';
import InfoTip from '../../../../components/ui/InfoTip';
import {
  BODY,
  BRANDY,
  BRANDY_FRAME,
  DANGER,
  DONE,
  FONT,
  LABEL,
  LINE,
  SURFACE,
  TEXT,
  TEXT_2,
  TEXT_3,
  TEXT_4,
  TEXTAREA_SX,
  WELL
} from '../capability-setup/designSystem';
import { LEVEL_COLOR, LEVEL_NAME } from '../Program/components/learning-lifecycle/CapabilityGapsList';
import type { CapabilityLevel } from '../Program/components/learning-lifecycle/teamCapability';
import type { CandidateStatus, CopyBlock, RoundStatus } from '../../../../api/hiring';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

/** **bold** is the only mark-up the server's copy uses. */
export const Rich: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith('**') && part.endsWith('**') ? (
        <Box component="strong" key={i} sx={{ color: TEXT, fontWeight: 600 }}>
          {part.slice(2, -2)}
        </Box>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    )}
  </>
);

/**
 * A block of the wording that is under legal review (C-2, H-8, H-28, L-11). It
 * always comes from the server, never from a string in this repo, so legal can
 * change it without a deploy, and the version it was read at travels with the
 * acknowledgement.
 */
export const CopyLines: React.FC<{ block: Pick<CopyBlock, 'lines'>; small?: boolean; bullets?: boolean }> = ({
  block,
  small = false,
  bullets = false
}) => (
  <Box
    component={bullets ? 'ul' : 'div'}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: small ? '8px' : '10px',
      ...(bullets ? { m: 0, pl: '18px' } : null)
    }}
  >
    {block.lines.map((line, i) => (
      <Typography key={i} component={bullets ? 'li' : 'p'} sx={{ ...BODY, fontSize: small ? 12.5 : 13, color: TEXT_2 }}>
        <Rich text={line} />
      </Typography>
    ))}
  </Box>
);

/** A capability's level, in the colours the rest of the product uses for them. */
export const LevelPill: React.FC<{ level: CapabilityLevel | null }> = ({ level }) => (
  <Box
    sx={{
      flexShrink: 0,
      px: '8px',
      height: 20,
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: '3px',
      border: `1px solid ${level ? LEVEL_COLOR[level] : TEXT_4}`,
      color: level ? LEVEL_COLOR[level] : TEXT_3,
      fontFamily: FONT,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.02em'
    }}
  >
    {/* A capability nobody was assessed on reads as a dash, never as 0% or as Weak. */}
    {level ? LEVEL_NAME[level] : 'Not assessed'}
  </Box>
);

/** A level in a table cell, as Insights shows one: its colour, in capitals, with no outline. */
export const LevelText: React.FC<{ level: CapabilityLevel | null }> = ({ level }) => (
  <Typography
    sx={{
      fontFamily: FONT,
      fontSize: level ? 11 : 10.5,
      fontWeight: level ? 700 : 500,
      letterSpacing: level ? '0.04em' : 0,
      textTransform: level ? 'uppercase' : 'none',
      textAlign: 'center',
      color: level ? LEVEL_COLOR[level] : TEXT_3
    }}
  >
    {level ? LEVEL_NAME[level] : 'Not assessed'}
  </Typography>
);

// The lines Insights draws in its tables (LevelMatrix): grey between rows and between columns.
export const GRID_LINE = '1px solid #262626';
export const HEAD_LINE = '1px solid #2A2A2A';
// The round page's buttons line up in three columns: ← Hiring over Save Ranking, Export over
// Compare Candidates, Close Round over Invite Candidates. The header sits outside the tab bar, so it stands
// in by the bar's border and padding to meet them.
export const ROUND_BUTTON_WIDTHS = { left: 170, right: 160 };
export const PANEL_INSET = 15;
/** Holds a button (or nothing) at a set width. */
export const ButtonSlot: React.FC<{ width: number; children?: React.ReactNode }> = ({ width, children }) => (
  <Box sx={{ width, flexShrink: 0, display: 'flex', '& > span, & button': { width: '100%' } }}>{children}</Box>
);
/** A table row's action: a small outline with no fill, so a table of them stays quiet. */
export const ROW_BUTTON_WIDTH = 72;
export const RowButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  brandy?: boolean;
  disabled?: boolean;
}> = ({ children, onClick, brandy = false, disabled = false }) => (
  <Box
    component="button"
    type="button"
    disabled={disabled}
    onClick={onClick}
    sx={{
      width: ROW_BUTTON_WIDTH,
      height: 26,
      flexShrink: 0,
      p: 0,
      bgcolor: 'transparent',
      border: `1px solid ${brandy ? BRANDY : '#3A3A3A'}`,
      borderRadius: '4px',
      fontFamily: FONT,
      fontSize: 12,
      fontWeight: 500,
      color: brandy ? BRANDY : TEXT_2,
      cursor: 'pointer',
      transition: 'background-color 0.15s, border-color 0.15s',
      '&:hover': brandy
        ? { bgcolor: 'rgba(247, 148, 32, 0.1)' }
        : { borderColor: TEXT_3, bgcolor: 'rgba(255, 255, 255, 0.04)' },
      '&:disabled': { opacity: 0.5, cursor: 'default' }
    }}
  >
    {children}
  </Box>
);
/** The brandy-outlined panel Insights puts its table in, filling what is left of the screen. */
export const PANEL_SX = {
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  p: '16px',
  display: 'flex',
  flexDirection: 'column',
  bgcolor: '#1C1C1C',
  border: '1px solid rgba(247, 148, 32, 0.45)',
  borderRadius: '8px',
  boxShadow: '0 0 28px rgba(247, 148, 32, 0.07)',
  [MOBILE_SX]: { flex: '0 0 auto', overflow: 'visible', p: '12px' }
};

const STATUS_WORDS: Record<CandidateStatus, { label: string; color: string }> = {
  invited: { label: 'Invited', color: TEXT_3 },
  started: { label: 'In progress', color: BRANDY },
  completed: { label: 'Completed', color: DONE },
  hired: { label: 'Hired', color: DONE },
  unsuccessful: { label: 'Unsuccessful', color: TEXT_3 },
  removed: { label: 'Removed', color: DANGER },
  expired: { label: 'Invite expired', color: TEXT_4 }
};

/** A candidate's status and a level in words, for what is not drawn on screen (the exports). */
export const statusLabel = (status: CandidateStatus): string => (STATUS_WORDS[status] || { label: status }).label;
export const levelName = (level: CapabilityLevel | null): string => (level ? LEVEL_NAME[level] : 'Not assessed');

/** A candidate's status as plain coloured text, for a table cell or beside a name. */
export const StatusText: React.FC<{ status: CandidateStatus }> = ({ status }) => {
  const word = STATUS_WORDS[status] || { label: status, color: TEXT_3 };
  return (
    <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: word.color, whiteSpace: 'nowrap' }}>
      {word.label}
    </Typography>
  );
};

export const StatusChip: React.FC<{ status: CandidateStatus }> = ({ status }) => {
  const word = STATUS_WORDS[status] || { label: status, color: TEXT_3 };
  return (
    <Box
      sx={{
        flexShrink: 0,
        px: '8px',
        height: 20,
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '3px',
        bgcolor: WELL,
        border: `1px solid ${LINE}`,
        color: word.color,
        fontFamily: FONT,
        fontSize: 11,
        fontWeight: 600
      }}
    >
      {word.label}
    </Box>
  );
};

const ROUND_WORDS: Record<RoundStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: TEXT_3 },
  open: { label: 'Open', color: BRANDY },
  closed: { label: 'Closed', color: TEXT_4 }
};

export const RoundStatusChip: React.FC<{ status: RoundStatus }> = ({ status }) => {
  const word = ROUND_WORDS[status];
  return (
    <Box
      sx={{
        flexShrink: 0,
        px: '8px',
        height: 20,
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '3px',
        bgcolor: WELL,
        border: `1px solid ${LINE}`,
        color: word.color,
        fontFamily: FONT,
        fontSize: 11,
        fontWeight: 600
      }}
    >
      {word.label}
    </Box>
  );
};

/**
 * L-6 and the compliance table's row 11. Response time and input mode are
 * information for the reviewer and never part of a level or an analysis, and
 * the UI has to say so wherever it shows them.
 */
export const IntegrityCaption: React.FC<{ caption?: string }> = ({ caption }) => (
  <Typography sx={{ ...BODY, fontSize: 11.5, color: TEXT_4, fontStyle: 'italic' }}>
    {caption || 'Context, not evidence. Never part of a level or the analysis.'}
  </Typography>
);

const LINE_DIVIDE = { width: '1px', height: 14, flexShrink: 0, bgcolor: '#3A3A3A' };

/** One fact about how a roleplay went, with what it does and doesn't mean on hover. */
const ContextPoint: React.FC<{ children: React.ReactNode; tip?: string }> = ({ children, tip }) => (
  <Box sx={{ ...BODY, fontSize: 12, color: TEXT_3, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
    {tip && <InfoTip title={tip} placement="top" />}
    {children}
  </Box>
);

/**
 * H-25, beside the level it belongs to, so the person making the decision sees
 * it. Context for a person to weigh, never part of a level (L-6).
 */
export const RoleplayContext: React.FC<{
  capability: {
    attemptsUsed: number;
    inputMode?: string | null;
    signals: { pasteCount: number; copyCount: number; tabSwitches: number };
  };
  attempts: number;
}> = ({ capability, attempts }) => (
  <>
    <ContextPoint>
      Attempts Used: {capability.attemptsUsed}/{attempts}
    </ContextPoint>
    <Box sx={LINE_DIVIDE} />
    <ContextPoint>
      {capability.inputMode === 'voice' ? 'Voice' : capability.inputMode === 'text' ? 'Typed' : '-'}
    </ContextPoint>
    <Box sx={LINE_DIVIDE} />
    <ContextPoint tip="This simply relays how many times the candidate copied text from the assessment or pasted text into the answer box. People copy and paste for many reasons, such as using their own notes.">
      Copy &amp; Pastes: {capability.signals.pasteCount + capability.signals.copyCount}
    </ContextPoint>
    <Box sx={LINE_DIVIDE} />
    <ContextPoint tip="This simply relays how many times the candidate left the assessment page, including briefly switching to another tab. It doesn't mean they abandoned the assessment. People leave the page for many reasons.">
      Dropoffs: {capability.signals.tabSwitches}
    </ContextPoint>
  </>
);

/**
 * The decision rationale Hire and Close require (L-3, H-26, H-27). The tooltip
 * text comes from the server with the rest of the legal copy.
 *
 * It is not a formality: it is the record that a person, not Elliott, made the
 * decision, and the candidate can ask for it.
 */
export const RationaleField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  tooltip?: CopyBlock | null;
  label?: string;
}> = ({ value, onChange, tooltip, label = 'Why are you making this decision?' }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <Typography sx={{ ...LABEL, color: TEXT_2 }}>
        {label}
        <Box component="span" aria-label="required" sx={{ color: BRANDY, ml: '4px' }}>
          *
        </Box>
      </Typography>
      <InfoTip title={(tooltip?.lines || []).join(' ')} />
    </Box>
    <Box
      component="textarea"
      rows={3}
      value={value}
      placeholder="A sentence or two is enough."
      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
      sx={TEXTAREA_SX}
    />
  </Box>
);

/** A labelled line in a profile or a summary. */
export const Detail: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
    <Typography sx={{ ...LABEL, color: TEXT_3 }}>{label}</Typography>
    <Typography sx={{ ...BODY, color: TEXT, overflowWrap: 'anywhere' }}>{children}</Typography>
  </Box>
);

/** An empty state inside a card, in the same voice as the rest of the admin side. */
export const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      p: '20px',
      borderRadius: '4px',
      border: `1px dashed ${LINE}`,
      bgcolor: WELL,
      textAlign: 'center'
    }}
  >
    <Typography sx={{ ...BODY, color: TEXT_3 }}>{children}</Typography>
  </Box>
);

export const Panel: React.FC<{ children: React.ReactNode; sx?: object }> = ({ children, sx }) => (
  <Box
    sx={{
      bgcolor: SURFACE,
      border: `1px solid ${LINE}`,
      borderRadius: '6px',
      p: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      minWidth: 0,
      ...sx
    }}
  >
    {children}
  </Box>
);

/** The US states, for the hiring location and the residence question (PRD 8.1b). */
export const US_STATES: Array<{ id: string; name: string }> = [
  ['AL', 'Alabama'],
  ['AK', 'Alaska'],
  ['AZ', 'Arizona'],
  ['AR', 'Arkansas'],
  ['CA', 'California'],
  ['CO', 'Colorado'],
  ['CT', 'Connecticut'],
  ['DE', 'Delaware'],
  ['DC', 'District of Columbia'],
  ['FL', 'Florida'],
  ['GA', 'Georgia'],
  ['HI', 'Hawaii'],
  ['ID', 'Idaho'],
  ['IL', 'Illinois'],
  ['IN', 'Indiana'],
  ['IA', 'Iowa'],
  ['KS', 'Kansas'],
  ['KY', 'Kentucky'],
  ['LA', 'Louisiana'],
  ['ME', 'Maine'],
  ['MD', 'Maryland'],
  ['MA', 'Massachusetts'],
  ['MI', 'Michigan'],
  ['MN', 'Minnesota'],
  ['MS', 'Mississippi'],
  ['MO', 'Missouri'],
  ['MT', 'Montana'],
  ['NE', 'Nebraska'],
  ['NV', 'Nevada'],
  ['NH', 'New Hampshire'],
  ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'],
  ['NY', 'New York'],
  ['NC', 'North Carolina'],
  ['ND', 'North Dakota'],
  ['OH', 'Ohio'],
  ['OK', 'Oklahoma'],
  ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'],
  ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'],
  ['SD', 'South Dakota'],
  ['TN', 'Tennessee'],
  ['TX', 'Texas'],
  ['UT', 'Utah'],
  ['VT', 'Vermont'],
  ['VA', 'Virginia'],
  ['WA', 'Washington'],
  ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'],
  ['WY', 'Wyoming']
].map(([id, name]) => ({ id, name }));

/** How a time reads in the Activity view and on a profile. */
export const when = (ms: number | null | undefined): string =>
  ms ? new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '-';

export const day = (ms: number | null | undefined): string =>
  ms ? new Date(ms).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '-';

/** A response time as the transcript shows it. */
export const seconds = (value: number | null | undefined): string =>
  value === null || value === undefined ? '-' : value < 60 ? `${value}s` : `${Math.floor(value / 60)}m ${value % 60}s`;

/**
 * The integrity counts beside one answer, as plain counts (H-25).
 *
 * Nothing is inferred from them here and nothing should be inferred from them
 * anywhere: they are context for a person reading the whole transcript, under
 * the caption that says exactly that (L-6). They were recorded from the first
 * build and dropped before they reached this screen until 26 Oct 2026, so the
 * candidate was told we kept signals the hiring manager never got to see.
 *
 * Deliberately NOT a probability that an answer was written by an AI. Those
 * classifiers are unreliable, and they misfire hardest on people who do not
 * write English as a first language — in hiring that is discrimination with a
 * number next to it, and the number would be read as a verdict whatever the
 * caption said.
 */
export const signals = (exchange: { pasteCount?: number; copyCount?: number; tabSwitches?: number }): string[] => {
  const parts: string[] = [];
  if (exchange.pasteCount) parts.push(`Pasted ${exchange.pasteCount}×`);
  if (exchange.copyCount) parts.push(`Copied from the page ${exchange.copyCount}×`);
  if (exchange.tabSwitches) parts.push(`Left the tab ${exchange.tabSwitches}×`);
  return parts;
};
